import { useEffect, useState, useCallback, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { deleteAuthorized, getAuthorized, isUserDoctor, postAuthorized } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import {
    SearchIcon,
    PlusIcon,
    EditIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XIcon,
    AlertIcon,
    FolderOpenIcon
} from "../../../components/icons";

import "./surveys.css";

dayjs.extend(relativeTime);
dayjs.locale("ru");

// Status Config
const StatusConfig = {
    draft: { 
        label: "Черновик", 
        className: "status-draft",
        icon: "📝"
    },
    archived: { 
        label: "Архив", 
        className: "status-archive",
        icon: "📦"
    },
    active: { 
        label: "Активный", 
        className: "status-active",
        icon: "✅"
    }
};

// Search Component
function SearchBar({ value, onChange, placeholder }) {
    return (
        <div className="search-bar">
            <div className="search-icon">
                <SearchIcon />
            </div>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="search-input"
            />
            {value && (
                <button 
                    className="search-clear"
                    onClick={() => onChange('')}
                    aria-label="Очистить поиск"
                >
                    <XIcon />
                </button>
            )}
        </div>
    );
}

// Filter Tabs
function FilterTabs({ currentFilter, onFilterChange, counts }) {
    const filters = [
        { key: 'all', label: 'Все', count: counts.all },
        { key: 'active', label: 'Активные', count: counts.active },
        { key: 'draft', label: 'Черновики', count: counts.draft }
    ];

    return (
        <div className="filter-tabs">
            {filters.map(filter => (
                <button
                    key={filter.key}
                    className={`filter-tab ${currentFilter === filter.key ? 'active' : ''}`}
                    onClick={() => onFilterChange(filter.key)}
                >
                    {filter.label}
                    <span className="filter-count">{filter.count}</span>
                </button>
            ))}
        </div>
    );
}

// Create Survey Modal
function CreateSurveyModal({ isOpen, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { setErrorList } = useContext(ErrorHandlerContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
            setTitle("");
            setDescription("");
            setIsSubmitting(false);
        }
    }, [isOpen]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!title.trim()) {
            setErrorList(prev => [...prev, "Введите название опроса"]);
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await postAuthorized("/doctor/create_survey", {}, {
                params: { 
                    title: title.trim(), 
                    description: description.trim() 
                }
            });
            
            if (response.status >= 200 && response.status < 300) {
                const createdSurvey = response.data;
                onClose();
                // Navigate to editor with the created survey ID
                navigate(ROUTES.SURVEY_EDITOR, { 
                    viewTransition: true, 
                    state: { surveyId: createdSurvey.id } 
                });
            }
        } catch (error) {
            console.error("Failed to create survey:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при создании опроса"]);
        } finally {
            setIsSubmitting(false);
        }
    }, [title, description, onClose, setErrorList, navigate]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Создать новый опрос</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-group">
                        <label htmlFor="survey-title">Название *</label>
                        <input
                            id="survey-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Введите название опроса"
                            autoFocus
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="survey-description">Описание</label>
                        <textarea
                            id="survey-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Добавьте описание опроса (необязательно)"
                            rows={4}
                        />
                    </div>
                    
                    <div className="modal-actions">
                        <button 
                            type="button" 
                            className="btn-secondary"
                            onClick={onClose}
                        >
                            Отмена
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary"
                            disabled={isSubmitting || !title.trim()}
                        >
                            {isSubmitting ? "Создание..." : "Создать опрос"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Survey Card Component
function SurveyCard({ survey, onEdit, onDelete }) {
    const status = StatusConfig[survey.status] || StatusConfig.draft;
    const createdDate = dayjs(survey.creation_dt);
    const relativeTime = createdDate.fromNow();
    const absoluteTime = createdDate.format("DD.MM.YYYY HH:mm");

    return (
        <div className={`survey-card ${status.className}`}>
            <div className="survey-card-header">
                <div className="survey-card-status">
                    <span className="status-icon">{status.icon}</span>
                    <span className="status-label">{status.label}</span>
                </div>
                <div className="survey-card-date" title={absoluteTime}>
                    {relativeTime}
                </div>
            </div>

            <div className="survey-card-content">
                <h3 className="survey-card-title">{survey.title}</h3>
                <p className="survey-card-description">
                    {survey.description || "Нет описания"}
                </p>
            </div>

            <div className="survey-card-footer">
                <div className="survey-id-tag">#{survey.id}</div>
            </div>

            <div className="survey-card-actions">
                <button 
                    className="survey-btn edit"
                    onClick={() => onEdit(survey)}
                    title="Редактировать"
                >
                    <EditIcon />
                    <span>Изменить</span>
                </button>
                <button 
                    className="survey-btn delete"
                    onClick={() => onDelete(survey)}
                    title="Удалить"
                >
                    <TrashIcon />
                </button>
            </div>
        </div>
    );
}

// Delete Confirmation Modal
function DeleteConfirmModal({ survey, isOpen, onClose, onConfirm }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const { setErrorList } = useContext(ErrorHandlerContext);

    const handleDelete = useCallback(async () => {
        if (!survey) return;
        
        setIsDeleting(true);
        try {
            await deleteAuthorized("/doctor/delete_survey", {}, {
                params: { survey_id: survey.id }
            });
            onConfirm();
            onClose();
        } catch (error) {
            console.error("Failed to delete survey:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при удалении опроса"]);
        } finally {
            setIsDeleting(false);
        }
    }, [survey, onConfirm, onClose, setErrorList]);

    if (!isOpen || !survey) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertIcon />
                </div>
                <h3>Удалить опрос?</h3>
                <p>
                    Вы уверены, что хотите удалить опрос <strong>"{survey.title}"</strong>?
                    <br />
                    Это действие нельзя отменить.
                </p>
                <div className="modal-actions">
                    <button 
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Отмена
                    </button>
                    <button 
                        className="btn-danger"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Удаление..." : "Удалить"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange, hasNext, hasPrev }) {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div className="pagination">
            <button 
                className="pagination-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={!hasPrev}
                aria-label="Предыдущая страница"
            >
                <ChevronLeftIcon />
            </button>

            {startPage > 1 && (
                <>
                    <button className="pagination-number" onClick={() => onPageChange(1)}>1</button>
                    {startPage > 2 && <span className="pagination-ellipsis">...</span>}
                </>
            )}

            {pages.map(page => (
                <button
                    key={page}
                    className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                    onClick={() => onPageChange(page)}
                >
                    {page}
                </button>
            ))}

            {endPage < totalPages && (
                <>
                    {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
                    <button className="pagination-number" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                </>
            )}

            <button 
                className="pagination-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!hasNext}
                aria-label="Следующая страница"
            >
                <ChevronRightIcon />
            </button>
        </div>
    );
}

// Skeleton Card
function SurveySkeleton() {
    return (
        <div className="survey-card skeleton">
            <div className="skeleton-header"></div>
            <div className="skeleton-content">
                <div className="skeleton-title"></div>
                <div className="skeleton-description"></div>
            </div>
            <div className="skeleton-footer"></div>
        </div>
    );
}

// Main Surveys Page
function SurveysPage() {
    // SEO for the page
    const seoProps = {
        title: "Медицинские опросники",
        description: "Управление медицинскими опросниками. Создавайте, редактируйте и отслеживайте результаты опросов.",
        keywords: "медицинские опросники, анкеты, управление опросами",
        noindex: true
    };
    const { loading: userLoading, error, data } = useUser();
    const navigate = useNavigate();
    const [surveys, setSurveys] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [statusCounts, setStatusCounts] = useState({ all: 0, active: 0, draft: 0 });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [deleteSurvey, setDeleteSurvey] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const ITEMS_PER_PAGE = 9;

    // Auth check
    useEffect(() => {
        if (userLoading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        if (!isUserDoctor(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [userLoading, error, data, navigate]);

    // Fetch surveys with backend filtering and pagination
    const fetchSurveys = useCallback(async () => {
        setIsLoading(true);
        try {
            const st = (currentPage - 1) * ITEMS_PER_PAGE;
            const params = {
                st,
                limit: ITEMS_PER_PAGE
            };

            // Add search query if present
            if (searchQuery.trim()) {
                params.query = searchQuery.trim();
            }

            // Add status filters based on current filter
            if (statusFilter === 'all') {
                // Exclude archived from "all" view
                params.filter_exclude_statuses = ['archived'];
            } else if (statusFilter === 'active') {
                params.filter_statuses = ['active'];
            } else if (statusFilter === 'draft') {
                params.filter_statuses = ['draft'];
            }

            const response = await getAuthorized("/doctor/my_surveys", { params });
            setSurveys(response.data?.surveys || []);
            setTotalCount(response.data?.total_count || 0);
        } catch (err) {
            console.error("Failed to fetch surveys:", err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, searchQuery, statusFilter]);

    // Fetch counts for all tabs
    const fetchStatusCounts = useCallback(async () => {
        try {
            // Fetch all counts in parallel
            const [allRes, activeRes, draftRes] = await Promise.all([
                getAuthorized("/doctor/my_surveys", { 
                    params: { st: 0, limit: 1, filter_exclude_statuses: ['archived'] } 
                }),
                getAuthorized("/doctor/my_surveys", { 
                    params: { st: 0, limit: 1, filter_statuses: ['active'] } 
                }),
                getAuthorized("/doctor/my_surveys", { 
                    params: { st: 0, limit: 1, filter_statuses: ['draft'] } 
                })
            ]);

            setStatusCounts({
                all: allRes.data?.total_count || 0,
                active: activeRes.data?.total_count || 0,
                draft: draftRes.data?.total_count || 0
            });
        } catch (err) {
            console.error("Failed to fetch status counts:", err);
        }
    }, []);

    useEffect(() => {
        if (!userLoading && isUserDoctor(data)) {
            fetchSurveys();
        }
    }, [userLoading, data, fetchSurveys]);

    // Fetch counts on initial load
    useEffect(() => {
        if (!userLoading && isUserDoctor(data)) {
            fetchStatusCounts();
        }
    }, [userLoading, data, fetchStatusCounts]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, statusFilter]);

    // Pagination
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    const handleEdit = useCallback((survey) => {
        navigate(ROUTES.SURVEY_EDITOR, { 
            viewTransition: true, 
            state: { surveyId: survey.id } 
        });
    }, [navigate]);

    const handleDeleteClick = useCallback((survey) => {
        setDeleteSurvey(survey);
        setIsDeleteModalOpen(true);
    }, []);

    const handleSurveyDeleted = useCallback(() => {
        fetchSurveys();
        fetchStatusCounts();
    }, [fetchSurveys, fetchStatusCounts]);

    if (userLoading) {
        return (
            <>
                <SEO {...seoProps} />
                <ProfileNavigation isDoctor={true} />
                <div className="surveys-page">
                    <div className="surveys-container">
                        <div className="surveys-skeleton-grid">
                            {[...Array(6)].map((_, i) => <SurveySkeleton key={i} />)}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO {...seoProps} />
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            <div className="surveys-page">
                <div className="surveys-container">
                    {/* Header */}
                    <div className="surveys-header">
                        <div className="surveys-header-content">
                            <h1 className="surveys-title">Мои опросы</h1>
                            <p className="surveys-subtitle">
                                {statusCounts.all > 0 
                                    ? `Всего опросов: ${statusCounts.all}` 
                                    : 'Создайте свой первый опрос'
                                }
                            </p>
                        </div>
                        <button 
                            className="surveys-create-btn"
                            onClick={() => setIsCreateModalOpen(true)}
                        >
                            <PlusIcon />
                            Создать опрос
                        </button>
                    </div>

                    {/* Create Survey Modal */}
                    <CreateSurveyModal 
                        isOpen={isCreateModalOpen}
                        onClose={() => setIsCreateModalOpen(false)}
                    />

                    {/* Filters and Search */}
                    <div className="surveys-toolbar">
                        <FilterTabs 
                            currentFilter={statusFilter}
                            onFilterChange={setStatusFilter}
                            counts={statusCounts}
                        />
                        <div className="surveys-search">
                            <SearchBar 
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Поиск по названию или описанию..."
                            />
                        </div>
                    </div>

                    {/* Results Count */}
                    {(searchQuery || statusFilter !== 'all') && !isLoading && (
                        <div className="surveys-results-info">
                            Найдено: {totalCount} {totalCount === 1 ? 'опрос' : totalCount < 5 ? 'опроса' : 'опросов'}
                        </div>
                    )}

                    {/* Surveys Grid */}
                    {isLoading ? (
                        <div className="surveys-grid">
                            {[...Array(6)].map((_, i) => <SurveySkeleton key={i} />)}
                        </div>
                    ) : surveys.length > 0 ? (
                        <div className="surveys-grid">
                            {surveys.map((survey) => (
                                <SurveyCard
                                    key={survey.id}
                                    survey={survey}
                                    onEdit={handleEdit}
                                    onDelete={handleDeleteClick}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="surveys-empty">
                            <div className="surveys-empty-icon">
                                <FolderOpenIcon />
                            </div>
                            <h3>{searchQuery ? 'Опросы не найдены' : 'Нет опросов'}</h3>
                            <p>
                                {searchQuery 
                                    ? 'Попробуйте изменить параметры поиска' 
                                    : 'Создайте первый опрос, нажав кнопку выше'
                                }
                            </p>
                        </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            hasNext={currentPage < totalPages}
                            hasPrev={currentPage > 1}
                        />
                    )}
                </div>
            </div>

            {/* Modals */}
            <DeleteConfirmModal 
                survey={deleteSurvey}
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleSurveyDeleted}
            />
        </>
    );
}

// Legacy SurveyList component for backwards compatibility (used by Scheduler.js)
function SurveyList({ list = [], additional = [] }) {
    const getStatusConfig = (status) => {
        return StatusConfig[status] || StatusConfig.draft;
    };

    if (list.length === 0) {
        return <div className="empty-state">Нет доступных опросов</div>;
    }

    return (
        <table className="legacy-survey-table">
            <thead>
                <tr>
                    <th>Название</th>
                    <th>Статус</th>
                    <th>Создан</th>
                    {additional.map((_, index) => <th key={index}></th>)}
                </tr>
            </thead>
            <tbody>
                {list.map((survey) => {
                    const status = getStatusConfig(survey.status);
                    const date = dayjs(survey.creation_dt);
                    
                    return (
                        <tr key={survey.id}>
                            <td data-label="Название:">{survey.title}</td>
                            <td data-label="Статус:">
                                <span className={`status-badge ${status.className}`}>
                                    {status.icon} {status.label}
                                </span>
                            </td>
                            <td data-label="Создан:">{date.format("DD.MM.YYYY")}</td>
                            {additional.map((Component, index) => (
                                <td key={index} data-label="">
                                    <Component survey={survey} />
                                </td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// Export wrapped with ErrorHandler
export default function SurveysPageWithErrorHandler() {
    return (
        <ErrorHandlerContextProvider>
            <SurveysPage />
            <ErrorHandler />
        </ErrorHandlerContextProvider>
    );
}

export { SurveyList };
